import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addMinutes, setHours, setMinutes, setSeconds, endOfWeek } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import {
    Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, Snackbar, Alert, Chip, IconButton,
    Divider, Tooltip, Paper, CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';

import api from '../services/api';
import { confirmAcao, toastSuccess } from '../services/alerts';
import { mostrarErro } from '../services/erros';
import BuscaPaciente from "./BuscaPaciente";
import { isVisualizador } from '../services/auth';

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 0 }),
    getDay,
    locales: { 'pt-BR': ptBR },
});

const STATUS_CONFIG = {
    AGENDADA:  { label: 'Agendada',  color: '#1976d2', bg: '#e3f2fd' },
    REALIZADA: { label: 'Realizada', color: '#2e7d32', bg: '#e8f5e9' },
    FALTA:     { label: 'Falta',     color: '#e65100', bg: '#fff3e0' },
    CANCELADA: { label: 'Cancelada', color: '#c62828', bg: '#ffebee' },
};

const MESSAGES = {
    allDay: 'Dia inteiro', today: 'Hoje', previous: '‹', next: '›',
    month: 'Mês', week: 'Semana', day: 'Dia', agenda: 'Agenda',
    date: 'Data', time: 'Hora', event: 'Sessão', noEventsInRange: 'Nenhuma sessão neste período.',
};

// Função corrigida para lidar com datas e horas de forma mais robusta
function sessaoParaEvento(s) {
    const start = new Date(s.dataHora);
    const end = addMinutes(start, s.duracao || 50);
    return { id: s.id, title: s.nomePaciente || 'Paciente', start, end, resource: s };
}

function slotParaBackgroundEvento(slot) {
    const [h, m] = slot.horaInicio.split(':').map(Number);
    const [hf, mf] = slot.horaFim.split(':').map(Number);
    
    // Constrói a data a partir da string 'YYYY-MM-DD' para evitar problemas de fuso
    const baseDate = new Date(slot.data + 'T00:00:00');
    
    const start = setHours(setMinutes(setSeconds(baseDate, 0), m), h);
    const end = setHours(setMinutes(setSeconds(baseDate, 0), mf), hf);

    return {
        id: `slot-${slot.id}`,
        title: slot.disponivel ? 'Disponível' : 'Ocupado',
        start, end,
        resource: { ...slot, type: 'slot' },
    };
}

// Converte um objeto Date para o formato 'YYYY-MM-DDTHH:mm:ss' que o backend espera
function dateToApiFormat(d) {
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
}

// Converte um objeto Date para o formato 'YYYY-MM-DD'
function toLocalDateString(d) {
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const initialForm = {
    dataHora: '', duracao: 50, linkVideo: '',
    statusSessao: 'AGENDADA', modalidadeSessao: 'PRESENCIAL',
    idPaciente: '', idPsicologo: '', idSlot: null,
};

export default function Calendario() {
    const soLeitura = isVisualizador();
    const [searchParams] = useSearchParams();

    const [fetching, setFetching] = useState(true);
    const [params, setParams] = useState(null);
    const [eventos, setEventos] = useState([]);
    const [slots, setSlots] = useState([]);
    const [disponibilidades, setDisponibilidades] = useState([]);
    const [diasBloqueados, setDiasBloqueados] = useState(new Set());
    const [eventosFiltrados, setEventosFiltrados] = useState([]);
    const [pacienteDoForm, setPacienteDoForm] = useState(null);
    const [psicologo, setPsicologo] = useState(null);
    const [currentRange, setCurrentRange] = useState(null);
    const [currentView, setCurrentView] = useState('week');
    const [currentDate, setCurrentDate] = useState(new Date());

    // Filtros
    const [filtroStatus, setFiltroStatus] = useState([]);
    const [filtroModalidade, setFiltroModalidade] = useState('');
    const [filtroPaciente, setFiltroPaciente] = useState(null);
    const [showFiltros, setShowFiltros] = useState(false);

    // Dialogs
    const [createDialog, setCreateDialog] = useState(false);
    const [detailDialog, setDetailDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [formData, setFormData] = useState(initialForm);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [assinaturaPaciente, setAssinaturaPaciente] = useState(null); // null = não carregado, false = sem ativa


    useEffect(() => {
        carregarPsicologo();
        carregarParams();
        carregarDisponibilidades();
    }, []);

    // Recalcula dias bloqueados sempre que disponibilidades mudarem
    useEffect(() => {
        const diasAtivos = new Set(
            disponibilidades
                .filter(d => d.ativo)
                .map(d => d.diaSemana)
        );
        const todosDias = ['SEGUNDA', 'TERCA', 'QUARTA', 'QUINTA', 'SEXTA'];
        const bloqueados = new Set(todosDias.filter(d => !diasAtivos.has(d)));
        setDiasBloqueados(bloqueados);
    }, [disponibilidades]);

    // Filtros aplicados client-side
    useEffect(() => {
        let res = [...eventos];
        if (filtroStatus.length > 0) res = res.filter(e => filtroStatus.includes(e.resource?.statusSessao));
        if (filtroModalidade) res = res.filter(e => e.resource?.modalidadeSessao === filtroModalidade);
        if (filtroPaciente) res = res.filter(e => e.resource?.idPaciente === filtroPaciente.id_paciente);
        setEventosFiltrados(res);
    }, [eventos, filtroStatus, filtroModalidade, filtroPaciente]);

    // Pré-preenchimento vindo da Fila de Espera. O nome vem na própria query, então
    // não depende mais de ter a lista de pacientes carregada para resolver o id.
    useEffect(() => {
        const pacienteId = searchParams.get('pacienteId');
        if (!pacienteId) return;
        const paciente = {
            id_paciente: Number(pacienteId),
            nomeCompleto: searchParams.get('nomePaciente') || '',
        };
        setFormData(prev => ({
            ...prev,
            idPaciente: pacienteId,
            idPsicologo: psicologo?.id_psicologo || '',
            duracao: params?.duracaoPadraoSessaoMinutos || 50,
        }));
        setPacienteDoForm(paciente);
        setCreateDialog(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams, psicologo, params]);

    const carregarParams = async () => {
        try {
            const res = await api.get('/parametros');
            setParams(res.data);
        } catch { console.error('Erro ao carregar parâmetros'); }
    };

    const carregarDisponibilidades = async () => {
        try {
            const res = await api.get('/disponibilidade');
            const d = res.data;
            setDisponibilidades(Array.isArray(d) ? d : (d?.content ?? []));
        } catch { console.error('Erro ao carregar disponibilidades'); }
    };

    const carregarPsicologo = async () => {
        try {
            const res = await api.get('/psicologos');
            if (res.data.length > 0) setPsicologo(res.data[0]);
        } catch { console.error('Erro ao carregar psicólogo'); }
    };

    const carregarDados = useCallback(async (range) => {
        if (!range) return;
        try {
            let inicio, fim;
            if (Array.isArray(range)) { // Month view
                inicio = range[0];
                fim = range[range.length - 1];
            } else { // Week/Day view
                inicio = range.start;
                fim = range.end;
            }

            // Formato YYYY-MM-DD para o endpoint de slots
            const dateFmt = d => toLocalDateString(d);
            // Formato YYYY-MM-DDTHH:mm:ss para o endpoint de sessões
            const dateTimeFmt = d => dateToApiFormat(d);

            const [sessRes, slotRes] = await Promise.all([
                api.get('/sessoes/periodo', { params: { inicio: dateTimeFmt(inicio), fim: dateTimeFmt(fim) } }),
                api.get('/agenda/slots', { params: { inicio: dateFmt(inicio), fim: dateFmt(fim) } }),
            ]);

            setEventos(sessRes.data.map(sessaoParaEvento));
            setSlots(slotRes.data.map(slotParaBackgroundEvento));
        } catch { mostrarMensagem('Erro ao carregar dados da agenda.', 'error'); }
    }, []);

    const handleRangeChange = useCallback((range) => {
        setCurrentRange(range);
        carregarDados(range);
    }, [carregarDados]);

    const handleNavigate = useCallback((date) => {
        setCurrentDate(date);
    }, []);

    const handleViewChange = useCallback((view) => {
        setCurrentView(view);
    }, []);

    // Carga inicial — onRangeChange não dispara no primeiro render em modo controlado
    useEffect(() => {
        const start = startOfWeek(new Date(), { weekStartsOn: 0 });
        const end = endOfWeek(new Date(), { weekStartsOn: 0 });
        const range = { start, end };
        setCurrentRange(range);
        carregarDados(range).finally(() => setFetching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    const mapDiaSemana = useCallback((dow) => {
        const map = { 1: 'SEGUNDA', 2: 'TERCA', 3: 'QUARTA', 4: 'QUINTA', 5: 'SEXTA' };
        return map[dow] || null;
    }, []);

    const diaBloqueado = useCallback((date) => {
        const dow = date.getDay();
        if (dow === 0 || dow === 6) return true;
        const dia = mapDiaSemana(dow);
        return !dia || diasBloqueados.has(dia);
    }, [diasBloqueados, mapDiaSemana]);

    const isDentroDisponibilidade = useCallback((date) => {
        const dow = date.getDay();
        if (dow === 0 || dow === 6) return false;
        const diaNome = mapDiaSemana(dow);
        if (!diaNome) return false;
        const disp = disponibilidades.find(d => d.diaSemana === diaNome && d.ativo);
        if (!disp) return false;
        const [hI, mI] = disp.horaInicio.split(':').map(Number);
        const [hF, mF] = disp.horaFim.split(':').map(Number);
        const total = date.getHours() * 60 + date.getMinutes();
        return total >= hI * 60 + mI && total < hF * 60 + mF;
    }, [disponibilidades, mapDiaSemana]);

    // Lógica de seleção de slot corrigida para ser exata
    const findSlotNoHorario = (start) => {
        const startTime = start.getTime();
        return slots.find(s => s.resource.disponivel && s.start.getTime() === startTime);
    };

    const handleSelectSlot = ({ start }) => {
        if (soLeitura) return;
        if (diaBloqueado(start)) {
            mostrarMensagem('Nenhuma disponibilidade ativa para este dia. Configure na página de Disponibilidade.', 'warning');
            return;
        }

        const slotMatch = findSlotNoHorario(start);

        if (!slotMatch) {
            mostrarMensagem('Horário indisponível ou fora da grade. Clique em um horário verde.', 'warning');
            return;
        }

        setFormData({
            ...initialForm,
            dataHora: dateToApiFormat(start), // Envia no formato correto para a API
            duracao: params?.duracaoPadraoSessaoMinutos || 50,
            idPsicologo: psicologo?.id_psicologo || '',
            idSlot: slotMatch.resource.id, // Passa o ID exato do slot
        });
        setCreateDialog(true);
    };

    const handleSelectEvent = (event) => {
        if (event.resource?.type === 'slot') return;
        setSelectedEvent(event);
        setEditMode(false);
        setDetailDialog(true);
    };

    const handleSalvar = async () => {
        if (!formData.idPaciente || !formData.dataHora) {
            mostrarMensagem('Selecione paciente e data/hora.', 'warning');
            return;
        }
        try {
            const payload = {
                ...formData,
                duracao: Number(formData.duracao),
                idPaciente: Number(formData.idPaciente),
                idPsicologo: formData.idPsicologo ? Number(formData.idPsicologo) : null,
            };
            await api.post('/sessoes', payload);

            const filaId = searchParams.get('filaId');
            if (filaId) {
                await api.patch(`/fila-espera/${filaId}/status`, null, { params: { status: 'AGENDADO' } });
            }

            mostrarMensagem('Sessão agendada!');
            setCreateDialog(false);
            if (currentRange) carregarDados(currentRange);
        } catch (e) {
            const msg = e.response?.data?.message || 'Erro ao salvar sessão.';
            mostrarMensagem(msg, 'error');
        }
    };

    const handleAtualizar = async () => {
        if (!formData.idPaciente || !formData.dataHora) {
            mostrarMensagem('Selecione paciente e data/hora.', 'warning');
            return;
        }
        try {
            const payload = {
                ...formData,
                duracao: Number(formData.duracao),
                idPaciente: Number(formData.idPaciente),
                idPsicologo: formData.idPsicologo ? Number(formData.idPsicologo) : null,
            };
            await api.put(`/sessoes/${selectedEvent.id}`, payload, { skipGlobalError: true });
            toastSuccess('Sessão atualizada.');
            setDetailDialog(false);
            if (currentRange) carregarDados(currentRange);
        } catch (error) {
            // Choque de horário e sessão finalizada chegam como CONFLITO; edição
            // de registro já alterado por outro usuário, como CONCORRENCIA.
            mostrarErro(error, 'atualizar a sessão');
        }
    };

    const handleMudarStatus = async (novoStatus) => {
        try {
            await api.patch(`/sessoes/${selectedEvent.id}/status`, null, { params: { status: novoStatus } });
            mostrarMensagem('Status atualizado!');
            setDetailDialog(false);
            if (currentRange) carregarDados(currentRange);
        } catch (e) { mostrarMensagem(e?.response?.data?.message || 'Erro ao atualizar status.', 'error'); }
    };

    const handleDeletar = async () => {
        const ok = await confirmAcao({
            title: 'Remover esta sessão?',
            text: 'O horário volta a ficar livre na agenda.',
            confirmButtonText: 'Remover',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;

        try {
            await api.delete(`/sessoes/${selectedEvent.id}`, { skipGlobalError: true });
            toastSuccess('Sessão removida.');
            setDetailDialog(false);
            if (currentRange) carregarDados(currentRange);
        } catch (error) {
            mostrarErro(error, 'remover a sessão');
        }
    };

    const abrirEdicao = () => {
        const s = selectedEvent.resource;
        setFormData({
            dataHora: dateToApiFormat(new Date(s.dataHora)),
            duracao: s.duracao,
            linkVideo: s.linkVideo || '',
            statusSessao: s.statusSessao || 'AGENDADA',
            modalidadeSessao: s.modalidadeSessao || 'PRESENCIAL',
            idPaciente: s.idPaciente || '',
            idPsicologo: s.idPsicologo || psicologo?.id_psicologo || '',
            idSlot: s.idSlot || null, // Mantém o idSlot se existir
        });
        // O nome vem na própria sessão; o campo de busca não tem lista para
        // resolver o id.
        setPacienteDoForm(s.idPaciente
            ? { id_paciente: s.idPaciente, nomeCompleto: s.nomePaciente || '' }
            : null);
        setEditMode(true);
    };

    const minTime = React.useMemo(() => {
        if (!params?.horarioInicioExpediente) return new Date(0, 0, 0, 7, 0);
        const [h, m] = params.horarioInicioExpediente.split(':').map(Number);
        return new Date(0, 0, 0, h, m);
    }, [params]);

    const maxTime = React.useMemo(() => {
        if (!params?.horarioFimExpediente) return new Date(0, 0, 0, 21, 0);
        const [h, m] = params.horarioFimExpediente.split(':').map(Number);
        return new Date(0, 0, 0, h, m);
    }, [params]);

    const stepMinutos = params?.duracaoPadraoSessaoMinutos || 50;

    const eventStyleGetter = (event) => {
        if (event.resource?.type === 'slot') return { style: { display: 'none' } };
        const cfg = STATUS_CONFIG[event.resource?.statusSessao] || STATUS_CONFIG.AGENDADA;
        return {
            style: {
                backgroundColor: cfg.color, borderRadius: '4px',
                border: 'none', color: '#fff', fontSize: '0.8rem', padding: '2px 4px',
            }
        };
    };

    const dayPropGetter = (date) => {
        if (diaBloqueado(date)) {
            return {
                style: {
                    backgroundColor: '#f5f5f5',
                    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.04) 4px, rgba(0,0,0,0.04) 8px)',
                    cursor: 'not-allowed',
                }
            };
        }
        return {};
    };

    const slotPropGetter = useCallback((date) => {
        if (diaBloqueado(date)) {
            return {
                style: {
                    backgroundColor: '#f5f5f5',
                    backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(0,0,0,0.03) 4px, rgba(0,0,0,0.03) 8px)',
                    cursor: 'not-allowed',
                }
            };
        }
        const ms = date.getTime();
        const slotMatch = slots.find(s => ms >= s.start.getTime() && ms < s.end.getTime());
        if (slotMatch) {
            return slotMatch.resource.disponivel
                ? { style: { backgroundColor: 'rgba(26, 187, 156, 0.15)', borderLeft: '3px solid #1ABB9C' } }
                : { style: { backgroundColor: 'rgba(230, 81, 0, 0.08)', borderLeft: '3px solid #e65100' } };
        }
        if (isDentroDisponibilidade(date)) {
            return { style: { backgroundColor: 'rgba(26, 187, 156, 0.04)', borderLeft: '2px dashed rgba(26,187,156,0.25)' } };
        }
        return {};
    }, [slots, isDentroDisponibilidade, diaBloqueado]);

    const mostrarMensagem = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    const buscarAssinaturaPaciente = async (idPaciente) => {
        if (!idPaciente) { setAssinaturaPaciente(false); return; }
        try {
            const res = await api.get(`/assinaturas-paciente/paciente/${idPaciente}/ativa`);
            setAssinaturaPaciente(res.data || false);
        } catch {
            setAssinaturaPaciente(false);
        }
    };

    const sessao = selectedEvent?.resource;
    const statusCfg = sessao ? (STATUS_CONFIG[sessao.statusSessao] || STATUS_CONFIG.AGENDADA) : null;

    if (fetching) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress sx={{ color: '#1ABB9C' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Calendário</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Veja a legenda abaixo para interpretar as cores dos horários
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Tooltip title="Filtros avançados">
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={() => setShowFiltros(!showFiltros)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: showFiltros || filtroStatus.length > 0 || filtroModalidade || filtroPaciente ? 'primary.main' : '#e0e0e0',
                                    borderRadius: 2, p: 1.2,
                                    bgcolor: showFiltros || filtroStatus.length > 0 || filtroModalidade || filtroPaciente ? 'rgba(25,118,210,0.08)' : 'transparent',
                                    color: showFiltros || filtroStatus.length > 0 || filtroModalidade || filtroPaciente ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                {showFiltros ? <FilterListOffIcon /> : <FilterListIcon />}
                            </IconButton>
                            {(filtroStatus.length > 0 || filtroModalidade || filtroPaciente) && !showFiltros && (
                                <Box sx={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, bgcolor: 'error.main', borderRadius: '50%' }} />
                            )}
                        </Box>
                    </Tooltip>
                </Box>
            </Box>

            {/* Painel de Filtros */}
            {showFiltros && (
                <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Status</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                                    const ativo = filtroStatus.includes(key);
                                    return (
                                        <Chip key={key} label={cfg.label} size="small"
                                            onClick={() => setFiltroStatus(prev =>
                                                prev.includes(key) ? prev.filter(s => s !== key) : [...prev, key])}
                                            sx={{ bgcolor: ativo ? cfg.color : cfg.bg, color: ativo ? '#fff' : cfg.color, border: `1px solid ${cfg.color}`, fontWeight: 600, cursor: 'pointer' }}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Modalidade</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {['PRESENCIAL', 'ONLINE'].map(m => (
                                    <Chip key={m} label={m === 'PRESENCIAL' ? 'Presencial' : 'Online'} size="small"
                                        onClick={() => setFiltroModalidade(filtroModalidade === m ? '' : m)}
                                        variant={filtroModalidade === m ? 'filled' : 'outlined'}
                                        color={filtroModalidade === m ? 'primary' : 'default'}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <Box sx={{ minWidth: 220 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>Paciente</Typography>
                            <Box sx={{ minWidth: 200 }}>
                                <BuscaPaciente
                                    label=""
                                    value={filtroPaciente}
                                    onChange={(v) => setFiltroPaciente(v)}
                                    textFieldProps={{ placeholder: 'Todos' }}
                                />
                            </Box>
                        </Box>

                        {(filtroStatus.length > 0 || filtroModalidade || filtroPaciente) && (
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', pb: 0.5 }}>
                                <Button size="small" onClick={() => { setFiltroStatus([]); setFiltroModalidade(''); setFiltroPaciente(null); }}>
                                    Limpar filtros
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Paper>
            )}

            {/* Legenda */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <Chip key={key} label={cfg.label} size="small"
                        sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, border: `1px solid ${cfg.color}` }} />
                ))}
                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                <Chip label="Slot disponível" size="small"
                    sx={{ bgcolor: 'rgba(26, 187, 156, 0.18)', color: '#0d8a70', border: '1px solid rgba(26,187,156,0.5)', fontWeight: 600 }} />
                <Chip label="Slot ocupado" size="small"
                    sx={{ bgcolor: 'rgba(230, 81, 0, 0.10)', color: '#e65100', border: '1px solid rgba(230,81,0,0.4)', fontWeight: 600 }} />
                <Chip label="Dentro do horário (sem slot)" size="small"
                    sx={{ bgcolor: 'rgba(26, 187, 156, 0.05)', color: '#555', border: '1px dashed rgba(26,187,156,0.4)', fontWeight: 600 }} />
                <Chip label="Fora do horário / bloqueado" size="small"
                    sx={{ bgcolor: '#f5f5f5', color: '#999', border: '1px solid #ddd', fontWeight: 600 }} />
                {eventosFiltrados.length !== eventos.length && (
                    <Chip label={`${eventosFiltrados.length} de ${eventos.length} sessões`} size="small" color="primary" variant="outlined" />
                )}
            </Box>

            {/* Calendário */}
            <Box sx={{ flexGrow: 1, minHeight: 600 }}>
                <Calendar
                    localizer={localizer}
                    events={eventosFiltrados}
                    backgroundEvents={slots}
                    startAccessor="start"
                    endAccessor="end"
                    culture="pt-BR"
                    messages={MESSAGES}
                    selectable
                    onSelectSlot={handleSelectSlot}
                    onSelectEvent={handleSelectEvent}
                    onRangeChange={handleRangeChange}
                    eventPropGetter={eventStyleGetter}
                    dayPropGetter={dayPropGetter}
                    slotPropGetter={slotPropGetter}
                    date={currentDate}
                    onNavigate={handleNavigate}
                    view={currentView}
                    onView={handleViewChange}
                    views={['month', 'week', 'day', 'agenda']}
                    step={stepMinutos}
                    timeslots={1}
                    min={minTime}
                    max={maxTime}
                    style={{ height: '100%', minHeight: 600 }}
                />
            </Box>

            {/* Dialog: Nova Sessão */}
            <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    Nova Sessão
                    {formData.idSlot && (
                        <Chip label="Horário disponível" size="small" sx={{ ml: 1, bgcolor: 'rgba(26,187,156,0.15)', color: '#1ABB9C', border: '1px solid #1ABB9C' }} />
                    )}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        <BuscaPaciente
                            value={pacienteDoForm}
                            onChange={(v) => {
                                setPacienteDoForm(v);
                                setFormData({ ...formData, idPaciente: v ? v.id_paciente : '' });
                                buscarAssinaturaPaciente(v?.id_paciente);
                            }}
                            label="Paciente *"
                            size="medium"
                        />
                        {formData.idPaciente && assinaturaPaciente === false && (
                            <Alert severity="warning" sx={{ py: 0.5 }}>
                                Paciente sem plano ativo — sessão será cobrada como avulsa.
                            </Alert>
                        )}
                        {formData.idPaciente && assinaturaPaciente && assinaturaPaciente.sessoesRestantes === 0 && assinaturaPaciente.nomePlanoAtendimento !== null && (
                            <Alert severity="error" sx={{ py: 0.5 }}>
                                Plano ativo mas sem sessões restantes — renove o plano antes de agendar.
                            </Alert>
                        )}
                        {formData.idPaciente && assinaturaPaciente && assinaturaPaciente.sessoesRestantes > 0 && (
                            <Alert severity="info" sx={{ py: 0.5 }}>
                                {assinaturaPaciente.sessoesRestantes} sessão(ões) restante(s) no plano.
                            </Alert>
                        )}
                        <TextField fullWidth required label="Data e Hora" type="datetime-local"
                            value={formData.dataHora.substring(0, 16)}
                            onChange={e => setFormData({ ...formData, dataHora: e.target.value + ':00' })}
                            InputLabelProps={{ shrink: true }} />
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField fullWidth type="number" label="Duração (min)"
                                value={formData.duracao}
                                onChange={e => setFormData({ ...formData, duracao: e.target.value })}
                                inputProps={{ min: 10, max: 240 }} />
                            <TextField select fullWidth label="Modalidade"
                                value={formData.modalidadeSessao}
                                onChange={e => setFormData({ ...formData, modalidadeSessao: e.target.value })}>
                                <MenuItem value="PRESENCIAL">Presencial</MenuItem>
                                <MenuItem value="ONLINE">Online</MenuItem>
                            </TextField>
                        </Box>
                        {formData.modalidadeSessao === 'ONLINE' && (
                            <TextField fullWidth label="Link da Videochamada"
                                value={formData.linkVideo}
                                onChange={e => setFormData({ ...formData, linkVideo: e.target.value })} />
                        )}
                        <TextField select fullWidth label="Status"
                            value={formData.statusSessao}
                            onChange={e => setFormData({ ...formData, statusSessao: e.target.value })}>
                            {Object.entries(STATUS_CONFIG)
                                .filter(([k]) => k === 'AGENDADA' || k === 'REALIZADA')
                                .map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                        </TextField>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setCreateDialog(false)}>Cancelar</Button>
                    <Button onClick={handleSalvar} variant="contained">Agendar</Button>
                </DialogActions>
            </Dialog>

            {/* Dialog: Detalhe / Editar */}
            <Dialog open={detailDialog} onClose={() => { setDetailDialog(false); setEditMode(false); }} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 'bold' }}>
                    {editMode ? 'Editar Sessão' : 'Detalhes da Sessão'}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {!soLeitura && !editMode && <Tooltip title="Editar"><IconButton size="small" onClick={abrirEdicao}><EditIcon fontSize="small" /></IconButton></Tooltip>}
                        {!soLeitura && selectedEvent?.resource?.statusSessao === 'AGENDADA' && <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={handleDeletar}><DeleteIcon fontSize="small" /></IconButton></Tooltip>}
                        <IconButton size="small" onClick={() => { setDetailDialog(false); setEditMode(false); }}><CloseIcon fontSize="small" /></IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent>
                    {editMode ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <BuscaPaciente
                                label="Paciente *"
                                size="medium"
                                value={pacienteDoForm}
                                onChange={(v) => {
                                    setPacienteDoForm(v);
                                    setFormData({ ...formData, idPaciente: v ? v.id_paciente : '' });
                                }}
                            />
                            <TextField fullWidth required label="Data e Hora" type="datetime-local"
                                value={formData.dataHora.substring(0, 16)}
                                onChange={e => {
                                    const novaDataHora = e.target.value + ':00';
                                    const novoSlot = findSlotNoHorario(new Date(e.target.value));
                                    setFormData({ ...formData, dataHora: novaDataHora, idSlot: novoSlot ? novoSlot.resource.id : null });
                                }}
                                InputLabelProps={{ shrink: true }}
                                helperText={formData.idSlot ? '✓ Slot disponível encontrado' : 'Nenhum slot disponível neste horário'}
                                FormHelperTextProps={{ sx: { color: formData.idSlot ? 'success.main' : 'warning.main' } }}
                            />
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                <TextField fullWidth type="number" label="Duração (min)"
                                    value={formData.duracao}
                                    onChange={e => setFormData({ ...formData, duracao: e.target.value })}
                                    inputProps={{ min: 10, max: 240 }} />
                                <TextField select fullWidth label="Modalidade"
                                    value={formData.modalidadeSessao}
                                    onChange={e => setFormData({ ...formData, modalidadeSessao: e.target.value })}>
                                    <MenuItem value="PRESENCIAL">Presencial</MenuItem>
                                    <MenuItem value="ONLINE">Online</MenuItem>
                                </TextField>
                            </Box>
                            {formData.modalidadeSessao === 'ONLINE' && (
                                <TextField fullWidth label="Link da Videochamada"
                                    value={formData.linkVideo}
                                    onChange={e => setFormData({ ...formData, linkVideo: e.target.value })} />
                            )}
                        </Box>
                    ) : sessao ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={statusCfg.label}
                                    sx={{ bgcolor: statusCfg.bg, color: statusCfg.color, fontWeight: 700, border: `1px solid ${statusCfg.color}` }} />
                                <Typography variant="caption" color="text.secondary">
                                    {sessao.modalidadeSessao === 'ONLINE' ? 'Online' : 'Presencial'}
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Paciente</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{sessao.nomePaciente}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Psicólogo</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{sessao.nomePsicologo || '—'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Data e Hora</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {format(new Date(sessao.dataHora), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Duração</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{sessao.duracao} min</Typography>
                                </Box>
                            </Box>
                            {sessao.linkVideo && (
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Link</Typography>
                                    <Typography variant="body2" component="a" href={sessao.linkVideo} target="_blank"
                                        sx={{ display: 'block', color: 'primary.main', wordBreak: 'break-all' }}>
                                        {sessao.linkVideo}
                                    </Typography>
                                </Box>
                            )}
                            <Divider />
                            {!soLeitura && sessao.statusSessao === 'AGENDADA' && (
                                <>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Alterar status:</Typography>
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                        {Object.entries(STATUS_CONFIG).filter(([k]) => ['REALIZADA', 'CANCELADA', 'FALTA'].includes(k)).map(([k, v]) => (
                                            <Button key={k} size="small" variant="outlined"
                                                sx={{ color: v.color, borderColor: v.color, '&:hover': { bgcolor: v.bg, borderColor: v.color } }}
                                                onClick={() => handleMudarStatus(k)}>
                                                {v.label}
                                            </Button>
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Box>
                    ) : null}
                </DialogContent>

                {editMode && (
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setEditMode(false)}>Cancelar</Button>
                        <Button onClick={handleAtualizar} variant="contained">Salvar</Button>
                    </DialogActions>
                )}
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
